import type { EntryKind } from '@taler/api-client'
import { absMoney, type Money, negateMoney, signOfMoney } from '@taler/domain'
import { ref } from 'vue'

const STORAGE_KEY = 'taler.accountingView'

function read(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

const enabled = ref(read())

/** Opt-in raw debit/credit presentation; plain language is the default. */
export function useAccountingView() {
  function set(value: boolean): void {
    enabled.value = value
    try {
      window.localStorage.setItem(STORAGE_KEY, value ? '1' : '0')
    } catch {
      // preference simply does not persist
    }
  }
  return { accountingView: enabled, setAccountingView: set }
}

const CREDIT_NORMAL = new Set(['INCOME', 'EQUITY', 'LIABILITY'])

/** Plain view reads a credit-normal class's figures as the positive amount a person expects; ASSET and EXPENSE stay as stored. */
export function plainAmount(value: Money, accountClass: string | undefined, accountingView: boolean): Money {
  return !accountingView && accountClass !== undefined && CREDIT_NORMAL.has(accountClass)
    ? negateMoney(value)
    : value
}

/** Tone follows the sign of the figure shown, so colour and sign never disagree. */
export function signTone(amount: Money): 'positive' | 'negative' | undefined {
  const sign = signOfMoney(amount)
  return sign > 0 ? 'positive' : sign < 0 ? 'negative' : undefined
}

/**
 * Plain view signs an expense or income by where its money went: negative when it left the person's
 * accounts. `categorySign` is the sign of the category legs' own debit-positive sum, so a refund reads plus.
 */
export function moneyLeaves(kind: EntryKind | undefined, categorySign: -1 | 0 | 1): boolean {
  return categorySign === 0 ? kind !== 'INCOME' : categorySign > 0
}

export function flowMoney(kind: EntryKind | undefined, total: Money, categorySign: -1 | 0 | 1): Money {
  if (kind !== 'EXPENSE' && kind !== 'INCOME') return total
  const magnitude = absMoney(total)
  return moneyLeaves(kind, categorySign) ? negateMoney(magnitude) : magnitude
}
